import { GeoCBatch, TRiskLevel } from './compliance.types';
import { IDueDiligenceProcess } from './compliance.types';
import { IBasePartnerCompany } from './partner.types';
import { IClusterSite, IFarmSite } from './site.types';
import { GraphQlConnection } from './types';
import { IRawMaterial } from './component.types';
import { IDataset } from './dataset.types';
import { TIconName } from 'designSystem/Primitives/Icon/Icon';

export enum EGeoDataErrorCategory {
  EUDR_REQUIREMENT = 'EUDR_REQUIREMENT',
}

export enum EGeoDataEntity {
  DATASET = 'DATASET',
  SITE_SELECTION = 'SITE_SELECTION',
  CLUSTER = 'CLUSTER',
}

export const GEO_DATA_ENTITY_ICON_NAME: Record<EGeoDataEntity, TIconName> = {
  DATASET: 'polygon-data',
  SITE_SELECTION: 'site-placeholder',
  CLUSTER: 'site-cluster-placeholder',
};

export enum IGeoDataErrorEntity {
  SITE = 'SITE',
  PLOT = 'PLOT',
  GEO_DATA = 'GEO_DATA',
}

export type TGeoDataErrorEntity = `${IGeoDataErrorEntity}`;

export type TGeoDataEntity = `${EGeoDataEntity}`;

export enum EGeoDataStatus {
  CREATED = 'CREATED',
  PARSED = 'PARSED',
  PARSING_FAILED = 'PARSING_FAILED',
  MAPPED = 'MAPPED',
  MAPPING_FAILED = 'MAPPING_FAILED',
  VALIDATED = 'VALIDATED',
  VALIDATION_FAILED = 'VALIDATION_FAILED',
  NORMALISED = 'NORMALISED',
  NORMALISATION_FAILED = 'NORMALISATION_FAILED',
  VERIFICATION_FAILED = 'VERIFICATION_FAILED',
  COMPLETED = 'COMPLETED',
}

export type TGeoDataStatus = `${EGeoDataStatus}`;

export enum EGeoDataCollectionStatus {
  COLLECTING_GEO_DATA = 'COLLECTING_GEO_DATA',
  SELECTING_RISK_ANALYSIS = 'SELECTING_RISK_ANALYSIS',
  PENDING_RISK_ANALYSIS = 'PENDING_RISK_ANALYSIS',
  COMPLETED_RISK_ANALYSIS = 'COMPLETED_RISK_ANALYSIS',
}

export const GEO_DATA_STATUS: Record<EGeoDataStatus, string> = {
  [EGeoDataStatus.CREATED]: 'Created',
  [EGeoDataStatus.PARSED]: 'Parsed',
  [EGeoDataStatus.PARSING_FAILED]: 'Parsing Failed',
  [EGeoDataStatus.MAPPED]: 'Mapped',
  [EGeoDataStatus.MAPPING_FAILED]: 'Mapping Failed',
  [EGeoDataStatus.VALIDATED]: 'Validated',
  [EGeoDataStatus.VALIDATION_FAILED]: 'Validation Failed',
  [EGeoDataStatus.NORMALISED]: 'Normalised',
  [EGeoDataStatus.NORMALISATION_FAILED]: 'Normalisation Failed',
  [EGeoDataStatus.VERIFICATION_FAILED]: 'Verification Failed',
  [EGeoDataStatus.COMPLETED]: 'Completed',
};

export type TGeoDataCollectionStatus = `${EGeoDataCollectionStatus}`;

export interface IGeoDataIssue {
  geoDataId: string;
  siteId?: string;
  entityId?: string;
  entityType: TGeoDataErrorEntity;
  errorCategory: EGeoDataErrorCategory;
  errorMessage: string;
  errorAdditionalInfo?: string;
}

export interface IGeoData {
  id: string;
  createdById: string;
  dataType: TGeoDataEntity;
  rawMaterial?: IRawMaterial;
  originCountry: string;
  title: string;
  ownedBy?: IBasePartnerCompany;
  totalPlotsArea: number;
  datasetId?: string;
  siteClusterId?: string;
  status: TGeoDataStatus;
  issues?: IGeoDataIssue[];
  createdTimestamp: Date;
  modifiedTimestamp: string;
  createdBy?: IBasePartnerCompany;
  dataset?: IDataset;
  siteCluster?: IClusterSite;
  sites?: GraphQlConnection<IFarmSite>;
  geocBatches?: GeoCBatch[];
  geoDataProcesses?: IGeoDataProcess[];
  riskStatus?: TRiskLevel;
  siteSelectionId?: string;
  excludedSitesIds?: string[];
  totalOriginalSiteCount: number;
}

export interface IGeoDataProcess extends IDueDiligenceProcess {
  status: TGeoDataCollectionStatus;
  geoDatas: IGeoData[];
}
